import { Box, Button, Container, Stack, styled, Typography, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import { isString } from 'lodash';
import { ArrowDown, ArrowUp, Sliders } from 'react-feather';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

// Components
import IconFeather from 'src/components/common/Icon';
import RouterLinkNoUnderline from 'src/components/common/RouterLinkNoUnderline';
import TooltipFormLabel from 'src/components/common/TooltipFormLabel';
import WhiteBoxContent from 'src/components/common/WhiteBoxContent';
import Table from 'src/components/Table';
import { FORMAT_DATE_TIME } from 'src/utils/constants/config';
import { AuthRouteKeys } from 'src/utils/constants/routes';

// Utilities
import { SectionErrorBoundary } from 'src/components/ErrorBoundary/SectionErrorBoundary';
import { useQueryParams } from 'src/hooks/useQueryParam';
import { useQueryListPort } from 'src/services/queries/useQueryPort';
import { getArrayDefault } from 'src/utils/helpers/arrayHelpers';
import { formatNumberWithChar } from 'src/utils/helpers/number';
import { renderNumberOrDefault, renderTextOrDefault } from 'src/utils/helpers/stringHelpers';
import { FormType, useFormSearch } from 'src/zustand/useFormSearch';

const StyledBox = styled(Box)(({ theme }) => ({
  background: theme.palette.background.paper2,
  borderRadius: theme.shape.borderRadius * 2,
}));

const StyledContainer = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(3),
  paddingBottom: theme.spacing(7),
  marginTop: 8,
}));

const ListPorts = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const queryParams = useQueryParams();
  const [, setSearchParams] = useSearchParams();
  const { data, isFetching } = useQueryListPort({
    ...queryParams,
    country_code: isString(queryParams?.country_code) ? [queryParams?.country_code] : queryParams?.country_code,
    region: isString(queryParams?.region) ? [queryParams?.region] : queryParams?.region,
  });
  const { handleOpenSearch } = useFormSearch((state) => state);

  const currentDate = dayjs();
  const shortMonth = currentDate.format(FORMAT_DATE_TIME.SHORT_MONTH);
  const longMonth = currentDate.format(FORMAT_DATE_TIME.FULL_MONTH);
  const monthYear = `${currentDate.format(FORMAT_DATE_TIME.FULL_MONTH)}, ${currentDate.format(FORMAT_DATE_TIME.FULL_YEAR)}`;

  const diffExportValue = parseInt(data?.summary?.current_exports) - parseInt(data?.summary?.yearago_exports);
  const diffPortcallsValue = parseInt(data?.summary?.current_portcalls) - parseInt(data?.summary?.yearago_portcalls);
  const percent_export = ((diffExportValue / parseInt(data?.summary?.yearago_exports)) * 100).toFixed(1).toString();
  const percent_portcalls = ((diffPortcallsValue / parseInt(data?.summary?.yearago_portcalls)) * 100)
    .toFixed(1)
    .toString();

  const getColorTextByValue = (value: number) => {
    return value > 0 ? theme.palette.success.main : theme.palette.error.main;
  };

  const getArrowByValue = (value: number) => {
    return value >= 0 ? (
      <IconFeather
        icon={<ArrowUp />}
        muiProps={{
          color: 'success',
          fontSize: 'small',
        }}
      />
    ) : (
      <IconFeather
        icon={<ArrowDown />}
        muiProps={{
          color: 'error',
          fontSize: 'small',
        }}
      />
    );
  };

  const configColumn: ConfigCell<ITradeDataPortInfo>[] = [
    {
      disablePadding: false,
      id: 'rank',
      disableSort: true,
      label: t('port.rank'),
      cell: (data: ITradeDataPortInfo) => {
        return (
          <Typography variant="body2" color="text.secondary">
            {renderTextOrDefault(data.rank)}
          </Typography>
        );
      },
    },
    {
      disablePadding: false,
      id: 'port_name',
      disableSort: true,
      label: t('port.name'),
      cell: (data: ITradeDataPortInfo) => {
        return (
          <RouterLinkNoUnderline to={AuthRouteKeys.PortDetail.replace(':id?', data?.id || '')}>
            <Typography variant="body2" color="secondary">
              {renderTextOrDefault(data.port_name)}
            </Typography>
          </RouterLinkNoUnderline>
        );
      },
    },
    {
      disablePadding: false,
      id: 'locode',
      disableSort: true,
      label: t('port.locode'),
      cell: (data: ITradeDataPortInfo) => {
        return (
          <Typography variant="body2" color="text.secondary">
            {renderTextOrDefault(data.locode)}
          </Typography>
        );
      },
    },
    {
      disablePadding: false,
      id: 'last_import_teu',
      disableSort: true,
      label: (
        <>
          {shortMonth + ' ' + t('port.last_import_teu')}{' '}
          <TooltipFormLabel title={t('port.last_import_teu.tooltip').replace('{{month}}', longMonth)} />
        </>
      ),
      cell: (data: ITradeDataPortInfo) => {
        return (
          <Typography variant="body2" color="text.secondary">
            {renderTextOrDefault(data.last_import_teu)}
          </Typography>
        );
      },
    },
    {
      disablePadding: false,
      id: 'last_export_teu',
      disableSort: true,
      label: (
        <>
          {shortMonth + ' ' + t('port.last_export_teu')}{' '}
          <TooltipFormLabel title={t('port.last_export_teu.tooltip').replace('{{month}}', longMonth)} />
        </>
      ),
      cell: (data: ITradeDataPortInfo) => {
        return (
          <Typography variant="body2" color="text.secondary">
            {renderTextOrDefault(data.last_export_teu)}
          </Typography>
        );
      },
    },
    {
      disablePadding: false,
      id: 'transshipments',
      disableSort: true,
      label: (
        <>
          {t('port.transshipments')} <TooltipFormLabel title={t('port.transshipments.tooltip')} />
        </>
      ),
      cell: (data: ITradeDataPortInfo) => {
        return (
          <Typography variant="body2" color="text.secondary">
            {renderNumberOrDefault(data.transshipments) + '%'}
          </Typography>
        );
      },
    },
    {
      disablePadding: false,
      id: 'import_dwell_time',
      disableSort: true,
      label: (
        <>
          {t('port.import_dwell_time')} <TooltipFormLabel title={t('port.import_dwell_time.tooltip')} />
        </>
      ),
      cell: (data: ITradeDataPortInfo) => {
        return (
          <Typography variant="body2" color="text.secondary">
            {renderNumberOrDefault(data.import_dwell_time)}
          </Typography>
        );
      },
    },
    {
      disablePadding: false,
      id: 'export_dwell_time',
      disableSort: true,
      label: (
        <>
          {t('port.export_dwell_time')} <TooltipFormLabel title={t('port.export_dwell_time.tooltip')} />{' '}
        </>
      ),
      cell: (data: ITradeDataPortInfo) => {
        return (
          <Typography variant="body2" color="text.secondary">
            {renderNumberOrDefault(data.export_dwell_time)}
          </Typography>
        );
      },
    },
    {
      disablePadding: false,
      id: 'ts_dwell_time',
      disableSort: true,
      label: (
        <>
          {t('port.ts_dwell_time')} <TooltipFormLabel title={t('port.ts_dwell_time.tooltip')} />{' '}
        </>
      ),
      cell: (data: ITradeDataPortInfo) => {
        return (
          <Typography variant="body2" color="text.secondary">
            {renderNumberOrDefault(data.ts_dwell_time)}
          </Typography>
        );
      },
    },
    {
      disablePadding: false,
      id: 'delay_percent',
      disableSort: true,
      label: (
        <>
          {t('port.delay_percent')} <TooltipFormLabel title={t('port.delay_percent.tooltip')} />{' '}
        </>
      ),
      cell: (data: ITradeDataPortInfo) => {
        return (
          <Typography variant="body2" color="text.secondary">
            {renderNumberOrDefault(data.delay_percent) + '%'}
          </Typography>
        );
      },
    },
    {
      disablePadding: false,
      id: 'schedule',
      disableSort: true,
      label: (
        <>
          {t('port.schedule')} <TooltipFormLabel title={t('port.schedule.tooltip')} />
        </>
      ),
      cell: (data: ITradeDataPortInfo) => {
        return (
          <Typography variant="body2" color="text.secondary">
            {renderTextOrDefault(data.schedule)}
          </Typography>
        );
      },
    },
  ];
  return (
    <StyledContainer maxWidth="xl">
      <SectionErrorBoundary resetKey="ListPorts" sectionName="ListPorts">
        <WhiteBoxContent>
          {/* <Link to={PORT_URL_VIDEO} target="_blank">
            <Typography color={'secondary'} textAlign={'center'} variant="caption">
              Learn more
            </Typography>
          </Link> */}
          <Stack direction="column" spacing={3}>
            <Stack direction={'row'} flexWrap="wrap" justifyContent={'space-between'}>
              <Typography textAlign="left" variant="h5">
                {t('port.title_table')} <span style={{ color: '#422AFB' }}>{monthYear}</span>
              </Typography>
              <Button
                sx={{ mr: 1 }}
                onClick={() => {
                  handleOpenSearch(FormType.SearchPort);
                }}
                color="primary"
                startIcon={<IconFeather muiProps={{ fontSize: 'small' }} icon={<Sliders />} />}
                variant="outlined"
              >
                Filter
              </Button>
            </Stack>
            <Stack direction={'row'} flexWrap="wrap" justifyContent={'space-between'}>
              {!isFetching && (
                <Stack direction={'row'} spacing={3}>
                  <StyledBox display={'flex'} flexDirection="column" gap={1} padding={1.5} height="100%">
                    <Typography textAlign="left" color="text.secondary" variant="body2">
                      Exports
                    </Typography>
                    <Stack
                      direction={'row'}
                      flexWrap="wrap"
                      justifyContent={'space-between'}
                      alignItems={'flex-end'}
                      spacing={1.5}
                    >
                      <Typography variant="h5">{formatNumberWithChar(data?.summary?.current_exports)}</Typography>
                      <Typography textAlign="left">
                        <Typography variant="body2" color={getColorTextByValue(diffExportValue)} component="span">
                          {getArrowByValue(diffExportValue)}
                          {percent_export}
                          {'%'}
                        </Typography>{' '}
                        <Typography variant="caption" color="text.secondary" component="span">
                          year-over-year
                        </Typography>
                      </Typography>
                    </Stack>
                  </StyledBox>
                  <StyledBox
                    display={'flex'}
                    flexDirection="column"
                    gap={1}
                    padding={1.5}
                    marginRight={10}
                    height="100%"
                  >
                    <Typography textAlign="left" color="text.secondary" variant="body2">
                      Portcalls
                    </Typography>
                    <Stack
                      direction={'row'}
                      flexWrap="wrap"
                      justifyContent={'space-between'}
                      alignItems={'flex-end'}
                      spacing={1.5}
                    >
                      <Typography variant="h5">{formatNumberWithChar(data?.summary?.current_portcalls)}</Typography>
                      <Typography textAlign="left">
                        <Typography variant="body2" color={getColorTextByValue(diffPortcallsValue)} component="span">
                          {getArrowByValue(diffPortcallsValue)}
                          {percent_portcalls}
                          {'%'}
                        </Typography>{' '}
                        <Typography variant="caption" color="text.secondary" component="span">
                          year-over-year
                        </Typography>
                      </Typography>
                    </Stack>
                  </StyledBox>
                </Stack>
              )}
              {/* ------------ TODO: Highlight The Data ------------ */}
              {/* <StyledBox display={'flex'} flexDirection="column" gap={1} padding={1.5} height="100%">
              <Typography textAlign="left" variant="h5">
                {'Box 3'}
              </Typography>
            </StyledBox> */}
            </Stack>
            <Table
              tableMaxHeight={600}
              onChangePage={(value) => {
                setSearchParams((searchParams) => {
                  searchParams.set('offset', String(value));
                  return searchParams;
                });
              }}
              onChangePageSize={(value) =>
                setSearchParams((searchParams) => {
                  searchParams.set('offset', '0');
                  searchParams.set('limit', String(value));
                  return searchParams;
                })
              }
              loading={isFetching}
              page={data.page}
              pageSize={data.page_size}
              totalRecords={data.total_records}
              dataKey={'id'}
              data={getArrayDefault(data.data)}
              configColumn={configColumn}
            />
          </Stack>
        </WhiteBoxContent>
      </SectionErrorBoundary>
    </StyledContainer>
  );
};
export default ListPorts;
